import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useDocuments } from '@/hooks/useDocuments';
import { DOCUMENT_TYPE_LABELS, ENTITY_TYPE_LABELS } from '@/services/documents';
import DocumentModal from './components/DocumentModal';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function DocumentsPage() {
  const { data, loading, error, refetch } = useDocuments();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);

  const types = ['all', ...Array.from(new Set(data.map((d) => d.documentType)))];

  const filtered = data.filter((d) => {
    if (typeFilter !== 'all' && d.documentType !== typeFilter) return false;
    if (!search) return true;
    return d.fileUrl.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Documentos</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} documentos generados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nuevo Documento
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  typeFilter === t
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {t === 'all' ? 'Todos' : DOCUMENT_TYPE_LABELS[t] ?? t}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por URL..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Tipo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Entidad</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Archivo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Generado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-16 text-center">Ver</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando documentos...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((d) => (
                    <tr key={d.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-list-3-line text-accent-600"></i>
                          </div>
                          <p className="text-foreground-900 font-medium">{DOCUMENT_TYPE_LABELS[d.documentType] ?? d.documentType}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-foreground-600">{ENTITY_TYPE_LABELS[d.relatedEntityType] ?? d.relatedEntityType}</td>
                      <td className="px-5 py-3 text-foreground-600 max-w-[280px] truncate">{d.fileUrl}</td>
                      <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(d.createdAt)}</td>
                      <td className="px-5 py-3 text-center">
                        <a
                          href={d.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                          title="Abrir archivo"
                        >
                          <i className="ri-external-link-line text-foreground-500"></i>
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-file-list-3-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay documentos registrados.'
                  : 'No se encontraron documentos con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showNew && <DocumentModal onClose={() => setShowNew(false)} onSaved={refetch} />}
    </AdminLayout>
  );
}